import { Button } from '@/components/ui/button';
import {
	Drawer,
	DrawerTrigger,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerDescription,
	DrawerFooter,
	DrawerClose,
} from '@/components/ui/drawer';
import { Copy, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { List, ListSaved } from '../list';
import { toast } from 'sonner';
import useGetUser from '@/features/User/hooks/useGetUser';
import {
	Dialog,
	DialogTrigger,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
	DialogClose,
} from '@/components/ui/dialog';
import { useEffect, useState } from 'react';

type ListShareModalProps = {
	list: List | ListSaved;
	trigger: React.ReactNode;
};

const ListShareModal = ({ list, trigger }: ListShareModalProps) => {
	const { t } = useTranslation();
	const { user } = useGetUser();
	const [isMobile, setIsMobile] = useState(false);

	const owner = user?.id === list.fk_user;

	const link = `${window.origin}/shared/${list.public_uid}`;

	useEffect(() => {
		const checkIsMobile = () => {
			const isTouchDevice =
				'ontouchstart' in window || navigator.maxTouchPoints > 0;
			const isSmallScreen = window.innerWidth < 640;
			setIsMobile(isTouchDevice && isSmallScreen);
		};

		checkIsMobile();
		window.addEventListener('resize', checkIsMobile);

		return () => window.removeEventListener('resize', checkIsMobile);
	}, []);

	const copyLink = () => {
		navigator.clipboard.writeText(link);
		toast.success(t('list.share.copied'));
	};

	const share = async () => {
		const shareData = {
			title: 'Giftee',
			text: owner
				? t('list.share.message')
				: t('list.share.messageOther', {
						name: (list as ListSaved).user.firstname,
					}),
			url: link,
		};

		if (!navigator.share) {
			toast.error(t('error.generic'));
			return;
		}

		try {
			await navigator.share(shareData);
		} catch (error) {
			if (error instanceof Error && error.name === 'AbortError') {
				return;
			}
			console.error('Erreur lors du partage:', error);
			toast.error(t('error.generic'));
		}
	};

	if (!isMobile) {
		return (
			<Dialog>
				<DialogTrigger asChild>{trigger}</DialogTrigger>
				<DialogContent className="overflow-hidden sm:max-w-[425px]">
					<div className="mx-auto w-full max-w-sm">
						<DialogHeader>
							<DialogTitle className="text-left">
								{t('sharedList.share.action')}
							</DialogTitle>
							<DialogDescription className="text-left">
								{owner
									? t('list.share.description')
									: t('list.share.descriptionOther', {
											name: (list as ListSaved).user
												.firstname,
										})}
							</DialogDescription>
						</DialogHeader>
						<div className="flex flex-col gap-3 py-4">
							{!!navigator.share && (
								<>
									<Button className="w-full" onClick={share}>
										<Send className="mr-2 h-4 w-4" />
										{t('common.share')}
									</Button>
									<p className="text-center">
										{t('common.or')}
									</p>
								</>
							)}
						</div>
						<div className="mb-4">
							<p className="font-bold">{t('list.share.copy')}</p>
							<div className="flex items-center">
								<span
									onClick={() => window.open(link, '_blank')}
									className="block w-full cursor-pointer pr-4 underline"
								>
									{link}
								</span>
								<Copy
									className="mx-auto h-8 w-8 cursor-pointer"
									onClick={() => copyLink()}
								/>
							</div>
						</div>
						<DialogFooter>
							<DialogClose asChild>
								<Button className="w-full" variant="outline">
									{t('common.back')}
								</Button>
							</DialogClose>
						</DialogFooter>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Drawer>
			<DrawerTrigger asChild>{trigger}</DrawerTrigger>
			<DrawerContent
				onClick={(e) => {
					e.stopPropagation();
					e.preventDefault();
				}}
			>
				<div
					className="mx-auto w-full max-w-sm"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
					}}
				>
					<DrawerHeader>
						<DrawerTitle className="text-left">
							{t('sharedList.share.action')}
						</DrawerTitle>
						<DrawerDescription className="text-left">
							{owner
								? t('list.share.description')
								: t('list.share.descriptionOther', {
										name: (list as ListSaved).user
											.firstname,
									})}
						</DrawerDescription>
					</DrawerHeader>
					<div className="flex flex-col gap-3 p-4">
						{!!navigator.share && (
							<>
								<Button
									className="w-full touch-manipulation"
									onClick={share}
									style={{ touchAction: 'manipulation' }}
								>
									<Send className="mr-2 h-4 w-4" />
									{t('common.share')}
								</Button>
								<p className="text-center">{t('common.or')}</p>
							</>
						)}
						<div>
							<p className="font-bold">{t('list.share.copy')}</p>
							<div className="flex items-center">
								<span
									onClick={() => window.open(link, '_blank')}
									className="block w-full cursor-pointer pr-4 text-sm underline"
									style={{ touchAction: 'manipulation' }}
								>
									{link}
								</span>
								<Copy
									className="mx-auto h-8 w-8 cursor-pointer"
									onClick={() => copyLink()}
									style={{ touchAction: 'manipulation' }}
								/>
							</div>
						</div>
					</div>
					<DrawerFooter>
						<DrawerClose asChild>
							<Button variant="outline">
								{t('common.back')}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};

export default ListShareModal;
